import React, { useState, useEffect, useRef } from 'react'
import { fetchGalleryBySlug } from '../lib/cms'
import { imageUrl } from '../lib/images'

export default function GallerySection({ gallerySlug, title, subtitle }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeImageIndex, setActiveImageIndex] = useState(null)
  const scrollRef = useRef(null)

  useEffect(() => {
    let isMounted = true
    async function load() {
      try {
        setLoading(true)
        const data = await fetchGalleryBySlug(gallerySlug)
        if (isMounted) setGallery(data)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    if (gallerySlug) {
      load()
    }
    return () => {
      isMounted = false
    }
  }, [gallerySlug])

  const images = gallery?.images || []

  const scroll = (direction) => {
    if (!scrollRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
    if (direction === 'next') {
      if (scrollLeft >= scrollWidth - clientWidth - 10) {
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        scrollRef.current.scrollBy({ left: clientWidth * 0.75, behavior: 'smooth' })
      }
    } else {
      if (scrollLeft <= 10) {
        scrollRef.current.scrollTo({ left: scrollWidth, behavior: 'smooth' })
      } else {
        scrollRef.current.scrollBy({ left: -clientWidth * 0.75, behavior: 'smooth' })
      }
    }
  }

  // Close lightbox on Escape key
  useEffect(() => {
    function handleKeyDown(e) {
      if (activeImageIndex === null) return
      if (e.key === 'Escape') setActiveImageIndex(null)
      if (e.key === 'ArrowRight') {
        setActiveImageIndex((prev) => (prev + 1) % images.length)
      }
      if (e.key === 'ArrowLeft') {
        setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeImageIndex, images.length])

  if (loading && !gallery) {
    return (
      <div className="py-12 text-center text-[#6E635C]">
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#C25E27] border-t-transparent" />
        <p className="mt-2 text-xs">Indlæser galleri...</p>
      </div>
    )
  }

  if (!images || images.length === 0) return null

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 sm:mb-10 gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#241F1C]">
              {title || gallery?.title || 'Stemningsbilleder'}
            </h2>
            {(subtitle || gallery?.description) && (
              <p className="text-xs sm:text-sm md:text-base text-[#6E635C] mt-1.5 sm:mt-2">
                {subtitle || gallery?.description}
              </p>
            )}
          </div>

          {/* Navigation arrows (no boxes, clean icons) */}
          {images.length > 2 && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => scroll('prev')}
                aria-label="Forrige billeder"
                className="w-10 h-10 text-xl text-[#241F1C] hover:text-[#C25E27] flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scroll('next')}
                aria-label="Næste billeder"
                className="w-10 h-10 text-xl text-[#241F1C] hover:text-[#C25E27] flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {/* Carousel / track with smooth scroll */}
        <div
          ref={scrollRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((imgSrc, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveImageIndex(idx)}
              className="flex-none w-[calc(50%-6px)] sm:w-[calc(50%-8px)] md:w-[calc(25%-12px)] snap-start group relative aspect-[4/3] overflow-hidden bg-[#E6DFD5] cursor-pointer focus:outline-none transition-shadow duration-300 hover:shadow-xl"
              aria-label={`Åbn billede ${idx + 1} i fuld skærm`}
            >
              <img
                src={imageUrl(imgSrc, 'width=600,quality=80,format=auto')}
                alt={`${gallery?.title || 'Galleri'} foto ${idx + 1}`}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#241F1C]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="bg-[#241F1C]/80 text-white p-2.5 shadow-md">
                  <i className="fa-solid fa-magnifying-glass-plus text-sm" aria-hidden="true" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Touch-Friendly Lightbox Modal */}
      {activeImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-[#241F1C]/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActiveImageIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setActiveImageIndex(null)
            }}
            className="absolute top-4 right-4 z-50 text-white/80 hover:text-white bg-[#241F1C]/70 hover:bg-[#241F1C] p-3 transition-colors cursor-pointer"
            aria-label="Luk billedevisning"
          >
            <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
          </button>

          {/* Navigation previous */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length)
              }}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-50 text-white/80 hover:text-white bg-[#241F1C]/60 hover:bg-[#241F1C] p-3 sm:p-4 transition-colors cursor-pointer"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
            </button>
          )}

          {/* Large image container */}
          <div
            className="relative max-w-5xl max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageUrl(images[activeImageIndex], 'width=1600,quality=85,format=auto')}
              alt="Forstørret galleribillede"
              className="max-h-[85vh] w-auto max-w-full object-contain"
            />
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1">
              {activeImageIndex + 1} / {images.length}
            </div>
          </div>

          {/* Navigation next */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setActiveImageIndex((prev) => (prev + 1) % images.length)
              }}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-50 text-white/80 hover:text-white bg-[#241F1C]/60 hover:bg-[#241F1C] p-3 sm:p-4 transition-colors cursor-pointer"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </section>
  )
}
